import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { Head, Loader, Mini, ChartCard, NoData, tt } from "./shared";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell, AreaChart, Area, PieChart, Pie, Legend, CartesianGrid } from "recharts";
import { DollarSign, Receipt, UserPlus, Sparkles } from "lucide-react";

const PIE_COLORS = ["#D4AF37", "#60a5fa", "#34d399", "#f59e0b", "#a78bfa", "#f472b6", "#22d3ee", "#fb7185"];

export function Reports() {
  const [rep, setRep] = useState(null);
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/reports").then((r) => setRep(r.data)).catch(() => {});
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  if (!rep || !stats) return <Loader />;
  const rev = Object.entries(rep.revenue_by_month).sort().map(([m, v]) => ({ month: m.slice(2), revenue: Math.round(v) }));
  const leads = Object.entries(rep.leads_by_month).sort().map(([m, v]) => ({ month: m.slice(2), leads: v }));
  const cats = Object.entries(rep.templates_by_category).map(([name, value]) => ({ name, value }));
  const ai = Object.entries(rep.ai_by_feature).map(([name, value]) => ({ name, value }));

  return (
    <div>
      <Head title="Reports" desc="Performance analytics across revenue, leads, sales, and AI." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Mini label="Total Revenue" value={money(stats.revenue)} icon={DollarSign} />
        <Mini label="Outstanding" value={money(stats.outstanding)} icon={Receipt} />
        <Mini label="Total Leads" value={stats.requests} icon={UserPlus} />
        <Mini label="AI Generations" value={stats.ai_usage} icon={Sparkles} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <ChartCard title="Revenue by month">
          {rev.length ? (
            <ResponsiveContainer width="100%" height={260}><BarChart data={rev}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 11 }} /><YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <Tooltip contentStyle={tt} formatter={(v) => money(v)} cursor={{ fill: "#ffffff08" }} />
              <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>{rev.map((_, i) => <Cell key={i} fill="#D4AF37" />)}</Bar>
            </BarChart></ResponsiveContainer>
          ) : <NoData />}
        </ChartCard>

        <ChartCard title="Leads by month">
          {leads.length ? (
            <ResponsiveContainer width="100%" height={260}><AreaChart data={leads} margin={{ left: -18 }}>
              <defs><linearGradient id="ld" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#60a5fa" stopOpacity={0.5} /><stop offset="95%" stopColor="#60a5fa" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <Tooltip contentStyle={tt} cursor={{ fill: "#ffffff08" }} />
              <Area type="monotone" dataKey="leads" stroke="#60a5fa" strokeWidth={2} fill="url(#ld)" />
            </AreaChart></ResponsiveContainer>
          ) : <NoData />}
        </ChartCard>

        <ChartCard title="Templates by category">
          <ResponsiveContainer width="100%" height={260}><PieChart>
            <Pie data={cats} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={false}>
              {cats.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
            </Pie>
            <Tooltip contentStyle={tt} /><Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
          </PieChart></ResponsiveContainer>
        </ChartCard>

        <ChartCard title="AI usage by feature">
          {ai.length ? (
            <ResponsiveContainer width="100%" height={260}><PieChart>
              <Pie data={ai} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90}>
                {ai.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={tt} /><Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
            </PieChart></ResponsiveContainer>
          ) : <NoData text="Use the AI Studio to see usage analytics." />}
        </ChartCard>
      </div>
    </div>
  );
}
